import winston from 'winston';
const DEFAULT_SENSITIVE_KEYS = [
    'password',
    'pass',
    'pwd',
    'secret',
    'clientsecret',
    'client_secret',
    'token',
    'access_token',
    'accessToken',
    'refresh_token',
    'refreshToken',
    'id_token',
    'idToken',
    'authorization',
    'cookie',
    'set-cookie',
    'api_key',
    'apiKey',
    'x-api-key',
    'privatekey',
    'private_key',
    // PII (defense-in-depth; tune to your data model / debugging needs)
    'email',
    'phone',
    'mobile',
    'mobilephone',
    'firstname',
    'lastname',
    'dob',
    'ssn',
    'dbpassword',
    'connectionstring',
    'dsn'
];
const sensitiveKeySet = new Set(DEFAULT_SENSITIVE_KEYS.map((k) => k.toLowerCase()));
export const isSensitiveKey = (key) => sensitiveKeySet.has(key.toLowerCase());
export const maskString = (input) => {
    let s = input;
    // --- headers / tokens ---
    s = s.replace(/(\bBearer\s+)[A-Za-z0-9._\-+=/]+/gi, '$1[REDACTED]');
    s = s.replace(/(\bBasic\s+)[A-Za-z0-9+/=]+/gi, '$1[REDACTED]');
    // JWT (common shape)
    s = s.replace(/\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[REDACTED_JWT]');
    // AWS Access Key ID (AKIA... / ASIA...)
    s = s.replace(/\b(AKIA|ASIA)[0-9A-Z]{16}\b/g, '[REDACTED_AWS_KEY_ID]');
    // --- key/value secrets embedded in strings ---
    // JSON-style: "password":"secret"  OR  'password':'secret'
    s = s.replace(/(["'])(password|pass|pwd|secret|token|access_token|refresh_token|authorization)(["']\s*:\s*)(["'])(.*?)(\4)/gi, (_m, q1, key, mid, q2) => `${q1}${key}${mid}${q2}[REDACTED]${q2}`);
    // key=value or key: value (value may be quoted or not)
    s = s.replace(/\b(password|pass|pwd|secret|token|access_token|refresh_token|authorization)\b(\s*[:=]\s*)(["']?)([^"'\s,}]+)(\3)/gi, (_m, key, sep, quote) => `${key}${sep}${quote}[REDACTED]${quote}`);
    // phrases like: "password is ...."
    s = s.replace(/\b(password|pass|pwd|secret|token)\b(\s+is\s+)(["']?)(.*?)(\3)/gi, (_m, key, mid, quote) => `${key}${mid}${quote}[REDACTED]${quote}`);
    return s;
};
/**
 * Deeply redacts objects/arrays by key name + masks strings.
 * - Handles cycles via WeakSet
 * - Has depth guard to avoid huge logs
 */
export const redactDeep = (value, opts) => {
    const maxDepth = opts?.maxDepth ?? 8;
    const seen = new WeakSet();
    const walk = (v, depth) => {
        if (v == null)
            return v;
        if (typeof v === 'string')
            return maskString(v);
        if (typeof v !== 'object')
            return v;
        if (seen.has(v))
            return '[Circular]';
        seen.add(v);
        if (depth >= maxDepth)
            return '[Truncated]';
        if (Array.isArray(v))
            return v.map((x) => walk(x, depth + 1));
        // Preserve Error shape but redact its enumerable props and mask message/stack
        if (v instanceof Error) {
            return {
                name: v.name,
                message: maskString(String(v.message)),
                stack: v.stack ? maskString(String(v.stack)) : undefined,
                ...Object.fromEntries(Object.entries(v).map(([k, val]) => [k, isSensitiveKey(k) ? '[REDACTED]' : walk(val, depth + 1)]))
            };
        }
        const out = {};
        for (const [k, val] of Object.entries(v)) {
            out[k] = isSensitiveKey(k) ? '[REDACTED]' : walk(val, depth + 1);
        }
        return out;
    };
    return walk(value, 0);
};
// ---- winston format sanitizer ----
export const maskFormat = winston.format((info) => {
    // Mask message (string or object)
    if (typeof info.message === 'string')
        info.message = maskString(info.message);
    else
        info.message = redactDeep(info.message);
    // Mask metadata if present (winston.format.metadata adds it)
    const anyInfo = info;
    if (anyInfo.metadata)
        anyInfo.metadata = redactDeep(anyInfo.metadata);
    // Mask stack if present
    if (typeof anyInfo.stack === 'string')
        anyInfo.stack = maskString(anyInfo.stack);
    // Mask any extra top-level fields you might add later (optional but handy)
    for (const k of Object.keys(anyInfo)) {
        if (k === 'message' || k === 'metadata' || k === 'stack')
            continue;
        if (isSensitiveKey(k))
            anyInfo[k] = '[REDACTED]';
        else if (typeof anyInfo[k] === 'string')
            anyInfo[k] = maskString(anyInfo[k]);
    }
    return info;
});
